import { useState, useMemo } from 'react'
import {
  Accordion,
  Badge,
  Button,
  Group,
  Stack,
  Text,
  Checkbox,
  Flex,
  Box,
  TextInput,
} from '@mantine/core'
import { IconRefresh, IconSearch } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { ISchool, IFeature } from '../types'

interface SchoolFeaturesListProps {
  schools: ISchool[]
  globalFeatures: IFeature[]
  onAddFeatures: (schoolId: string, features: string[]) => Promise<void>
  onRemoveFeatures: (schoolId: string, features: string[]) => Promise<void>
  onReload: () => Promise<void>
}

export const SchoolFeaturesList = ({
  schools,
  globalFeatures,
  onAddFeatures,
  onRemoveFeatures,
  onReload,
}: SchoolFeaturesListProps) => {
  const { t } = useTranslation()
  const [loadingStates, setLoadingStates] = useState<{
    [key: string]: boolean
  }>({})
  const [searchTerm, setSearchTerm] = useState('')

  // Filter schools by school name OR feature name (like ILIKE in SQL)
  const filteredSchools = useMemo(() => {
    if (!searchTerm.trim()) {
      return schools
    }

    const searchLower = searchTerm.toLowerCase().trim()
    return schools.filter((school) => {
      // Search by school name
      const matchesSchoolName = school.name.toLowerCase().includes(searchLower)

      // Search by feature name (shows schools that have the searched feature)
      const matchesFeature = school.features?.some(feature =>
        feature.toLowerCase().includes(searchLower),
      )

      return matchesSchoolName || matchesFeature
    })
  }, [schools, searchTerm])

  const handleFeatureToggle = async (schoolId: string, featureName: string, checked: boolean) => {
    setLoadingStates((prev) => ({ ...prev, [schoolId]: true }))

    try {
      if (checked) {
        // Adicionar feature
        console.log('Adding feature to school', schoolId, ':', [featureName])
        await onAddFeatures(schoolId, [featureName])
      } else {
        // Remover feature
        console.log('Removing feature from school', schoolId, ':', [featureName])
        await onRemoveFeatures(schoolId, [featureName])
      }
    } catch (error) {
      console.error('Error toggling feature:', error)
      alert(t('feature_flags_error_updating'))
    } finally {
      setLoadingStates((prev) => ({ ...prev, [schoolId]: false }))
    }
  }

  const handleReload = async () => {
    setLoadingStates((prev) => ({ ...prev, reload: true }))
    try {
      await onReload()
    } finally {
      setLoadingStates((prev) => ({ ...prev, reload: false }))
    }
  }

  return (
    <Stack>
      <Flex justify="space-between" align="center">
        <Text size="lg" weight={600}>
          {t('feature_flags_schools_count', { count: filteredSchools.length })}
        </Text>
        <Button
          leftIcon={<IconRefresh size={16} />}
          variant="light"
          onClick={handleReload}
          loading={loadingStates.reload}
        >
          {t('feature_flags_reload')}
        </Button>
      </Flex>

      <TextInput
        placeholder={t('feature_flags_search_placeholder')}
        icon={<IconSearch size={16} />}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        mb="md"
      />

      <Accordion variant="separated">
        {filteredSchools.map((school) => (
          <Accordion.Item key={school.id} value={school.id.toString()}>
            <Accordion.Control>
              <Group position="apart">
                <Text weight={500}>{school.name}</Text>
                <Badge color="blue" variant="filled">
                  {t('feature_flags_features_count', {
                    active: school.features?.length || 0,
                    total: globalFeatures.length
                  })}
                </Badge>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack spacing="md">
                {globalFeatures.length > 0 ? (
                  <Box>
                    <Text size="sm" weight={600} mb="xs">
                      {t('feature_flags_available_features')}
                    </Text>
                    <Stack spacing="xs">
                      {globalFeatures.map((feature) => {
                        const isChecked = school.features?.includes(feature.key) || false
                        return (
                          <Checkbox
                            key={feature.key}
                            label={feature.key}
                            checked={isChecked}
                            onChange={(e) =>
                              handleFeatureToggle(school.id, feature.key, e.target.checked)
                            }
                            disabled={loadingStates[school.id]}
                          />
                        )
                      })}
                    </Stack>
                  </Box>
                ) : (
                  <Text size="sm" color="dimmed">
                    {t('feature_flags_no_features')}
                  </Text>
                )}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>

      {filteredSchools.length === 0 && (
        <Text align="center" color="dimmed" py="xl">
          {searchTerm ? t('feature_flags_no_results') : t('feature_flags_no_schools')}
        </Text>
      )}
    </Stack>
  )
}
